import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Input,
  Button,
  Toggle,
  Alert,
  Table,
  Badge,
  Box,
  TextFilter,
  Tabs,
  ExpandableSection,
  ColumnLayout
} from '@cloudscape-design/components';
import { dynamodb, config } from '../aws-config';

function Settings() {
  const [confidenceThreshold, setConfidenceThreshold] = useState('85');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [autoRefresh, setAutoRefresh] = useState('30');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [desktopNotifications, setDesktopNotifications] = useState(false);
  const [autoProcess, setAutoProcess] = useState(true);
  const [manualReview, setManualReview] = useState(false);
  const [alert, setAlert] = useState(null);
  
  // Business Rules state
  const [businessRules, setBusinessRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [rulesFilterText, setRulesFilterText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    // Load saved settings
    const savedThreshold = localStorage.getItem('confidenceThreshold');
    if (savedThreshold) setConfidenceThreshold(savedThreshold);
    
    // Load business rules
    loadBusinessRules();
  }, []);

  const loadBusinessRules = async () => {
    setLoadingRules(true);
    try {
      // In production, this would query the business-rules DynamoDB table
      // For now, using mock data structure
      const rulesTableName = config.businessRulesTable || 'form-processing-business-rules';
      
      const response = await dynamodb.scan({
        TableName: rulesTableName
      }).promise();
      
      setBusinessRules(response.Items || []);
    } catch (error) {
      console.error('Error loading business rules:', error);
      // Use fallback mock data if table doesn't exist yet
      setBusinessRules(getMockBusinessRules());
    } finally {
      setLoadingRules(false);
    }
  };

  const getMockBusinessRules = () => {
    // Complete set of business rules from rules.txt with form applicability
    // Forms: 1610/1 (Summary), 1610/2 (Officer W2-O6), 1610/5 (Officer O7-O8), 
    //        1610/7 (Chief E7-E9), 1610/19 (MIDP), 1610/20 (Mid-Term Counseling)
    return [
      // SECTION 1: Member Identification (Blocks 1-9)
      { ruleId: 'RULE-001', category: 'Member Identification', block: '1', active: true, primary: true, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Name/SSN Match Validation', description: "Verify block 1, Member's Name and block 4, Members SSN. The name in block 1 does not match SSN in block 4.", severity: 'CRITICAL' },
      { ruleId: 'RULE-002', category: 'Member Identification', block: '2', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Grade/Rate Consistency', description: 'Invalid summary group. Block 2, Grade/Rate, must be the same on all reports.', severity: 'CRITICAL' },
      { ruleId: 'RULE-003', category: 'Member Identification', block: '3', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Designator Competitive Category', description: 'Invalid summary group. Block 3, Designator, must be in the same competitive category on all reports.', severity: 'CRITICAL' },
      { ruleId: 'RULE-004', category: 'Member Identification', block: '4', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'SSN Required', description: "Block 4, Members SSN, is a required entry. The member's entire SSN must be included on the performance evaluation for accuracy in processing.", severity: 'CRITICAL' },
      { ruleId: 'RULE-005A', category: 'Member Identification', block: '5', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Duty/Competitive Status Required', description: 'Complete block 5, Duty/Competitive Status. This is a required entry that must be the same on all reports in a summary group.', severity: 'CRITICAL' },
      { ruleId: 'RULE-005B', category: 'Member Identification', block: '5', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Duty/Competitive Status Consistency', description: 'Invalid summary group. Block 5, Duty/Competitive Status, must be the same on all reports.', severity: 'CRITICAL' },
      { ruleId: 'RULE-006', category: 'Member Identification', block: '6', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'UIC Required', description: 'Complete block 6, UIC. This is a required entry.', severity: 'CRITICAL' },
      { ruleId: 'RULE-007', category: 'Member Identification', block: '7', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Ship/Station Required', description: 'Complete block 7, Ship/Station. This is a required entry.', severity: 'CRITICAL' },
      { ruleId: 'RULE-008', category: 'Member Identification', block: '8', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Promotion Status Consistency', description: 'Invalid summary group. Block 8, Promotion Status, must be the same on all reports.', severity: 'CRITICAL' },
      { ruleId: 'RULE-008A', category: 'Member Identification', block: '8', active: true, primary: true, enlisted: false, officer: true, chief: false, applicableForms: ['1610/2'], title: 'Promotion Status - SELECTED Invalid for O1', description: 'Invalid entry in block 8, Promotion Status. Entry of "SELECTED" not authorized for personnel in the grade of O1, Ensign.', severity: 'HIGH' },
      { ruleId: 'RULE-008B', category: 'Member Identification', block: '8', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Promotion Status - APPROVED Invalid', description: 'Invalid entry in block 8, Promotion Status. "APPROVED" is only an authorized entry for block 21, Billet Subcategory.', severity: 'HIGH' },
      { ruleId: 'RULE-008C', category: 'Member Identification', block: '8', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Promotion Status Required', description: 'Complete block 8, Promotion Status. This is a required entry.', severity: 'CRITICAL' },
      { ruleId: 'RULE-009', category: 'Member Identification', block: '9', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Date Reported Required', description: 'Entry required in block 9, Date Reported. This is a required entry.', severity: 'CRITICAL' },
      
      // SECTION 2: Report Occasion and Period (Blocks 10-15)
      { ruleId: 'RULE-010-013A', category: 'Report Occasion', block: '10-13', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Occasion for Report Required', description: 'Complete block 10-13, Occasion for Report. This is a required entry.', severity: 'CRITICAL' },
      { ruleId: 'RULE-010-013B', category: 'Report Occasion', block: '10-13', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Occasion for Report Consistency', description: 'Invalid summary group. Block 10-13, Occasion for Report, must be the same on all reports.', severity: 'CRITICAL' },
      { ruleId: 'RULE-010-013C', category: 'Report Occasion', block: '10-13', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Special Report Justification Required', description: 'Provide in block 41 or 43, Comments on Performance, justification for the submission of a Special report.', severity: 'HIGH' },
      { ruleId: 'RULE-010-013D', category: 'Report Occasion', block: '10-13', active: true, primary: true, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'Special FitRep Prohibited for Officers', description: 'Special fitness reports for superior performance or recommendations are specifically prohibited for officers.', severity: 'CRITICAL' },
      { ruleId: 'RULE-010-013E', category: 'Report Occasion', block: '10-13', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Invalid Special Report Justification', description: 'Invalid or Unauthorized justification for the submission of a Special report.', severity: 'HIGH' },
      { ruleId: 'RULE-010-013F', category: 'Report Occasion', block: '10-13', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Special Report Duration Invalid', description: 'Invalid "Special" report. Prepare a not observed detachment of individual report with comments for every period of active duty that is greater than 10 days and less than 90 days.', severity: 'MEDIUM' },
      { ruleId: 'RULE-014-015A', category: 'Report Occasion', block: '14-15', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Period of Report Required', description: 'Complete blocks 14 and 15, Period of Report. This is a required entry.', severity: 'CRITICAL' },
      { ruleId: 'RULE-014-015B', category: 'Report Occasion', block: '14-15', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Periodic Report Date Mismatch', description: 'Report(s) marked "Periodic" but "To" date (block 15) is a non-periodic date.', severity: 'HIGH' },
      { ruleId: 'RULE-014-015C', category: 'Report Occasion', block: '14-15', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Report Period Exceeds 15 Months', description: 'Period of report exceeds maximum authorized limit of 15 months.', severity: 'CRITICAL' },
      { ruleId: 'RULE-014-015D', category: 'Report Occasion', block: '14-15', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Report End Date Consistency', description: 'Invalid summary group. Block 15, Report end Date, must be the same on all reports.', severity: 'CRITICAL' },
      { ruleId: 'RULE-014-015E', category: 'Report Occasion', block: '14-15', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Invalid Report Dates', description: 'Correct blocks 14 and 15, Period of Report. Invalid dates entered. OR Block 15, report end date, must not be a future date.', severity: 'CRITICAL' },
      
      // SECTION 3: Not Observed Reports (Block 16)
      { ruleId: 'RULE-016A', category: 'Not Observed Reports', block: '16', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'NOB Cannot Be Adverse', description: 'Not Observed Reports (NOB) can not be adverse. Promotion recommendation required.', severity: 'HIGH' },
      { ruleId: 'RULE-016B', category: 'Not Observed Reports', block: '16', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'NOB with Failed PRT Before 1 MAY 2000', description: 'Report can not be marked Not Observed (NOB) if block 20 (PRT) indicates (F) or (NS) and the end date of the report is prior to 1 MAY 2000.', severity: 'HIGH' },
      { ruleId: 'RULE-016C', category: 'Not Observed Reports', block: '16', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Partial NOB Trait Limit', description: 'NOB reports that do not have a promotion recommendation, must not have more than three (3) graded trait marks.', severity: 'MEDIUM' },
      
      // SECTION 4: Report Type (Blocks 17-19)
      { ruleId: 'RULE-017-019', category: 'Report Type', block: '17-19', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Type of Report Required', description: 'Entry required in blocks 17 through 19, Type of Report.', severity: 'CRITICAL' },
      { ruleId: 'RULE-019A', category: 'Report Type', block: '19', active: true, primary: true, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'OPS CDR Justification Required', description: 'Justification for submitting an Operational Commander Report must be included in block 41, Comment on Performance.', severity: 'HIGH' },
      { ruleId: 'RULE-019B', category: 'Report Type', block: '19', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'OPS CDR Reporting Senior Title', description: 'Reporting senior title must be Commander or Officer in Charge in block 25 on an OPS CDR report.', severity: 'HIGH' },
      { ruleId: 'RULE-019C', category: 'Report Type', block: '19', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'OPS CDR Member Identification', description: 'Member must be identified as a Commander or Officer in Charge in block 21, 28, and/or block 29 on an OPS CDR report.', severity: 'HIGH' },
      
      // SECTION 5: Physical Readiness (Block 20)
      { ruleId: 'RULE-020A', category: 'Physical Readiness', block: '20', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Physical Readiness Required', description: 'Missing or invalid entry in block 20, Physical Readiness.', severity: 'HIGH' },
      { ruleId: 'RULE-020B', category: 'Physical Readiness', block: '20', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Exempt PRT Code Invalid', description: 'Invalid entry in block 20, Physical Readiness. "Exempt" is no longer a valid PRT Code. Refer to OPNAVINST 6110.1H.', severity: 'HIGH' },
      
      // SECTION 6: Billet Subcategory (Block 21)
      { ruleId: 'RULE-021A', category: 'Billet Subcategory', block: '21', active: true, primary: true, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Billet Subcategory Consistency', description: 'Invalid summary group. Block 21, Billet Subcategory, must be the same on all reports.', severity: 'CRITICAL' },
      { ruleId: 'RULE-021B', category: 'Billet Subcategory', block: '21', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Billet Subcategory Required', description: 'Complete block 21, Billet Subcategory. This is a required entry.', severity: 'HIGH' },
      { ruleId: 'RULE-021C', category: 'Billet Subcategory', block: '21', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Special Billet Subcategory Unauthorized', description: 'Unauthorized use of the special billet subcategory entry of "Special" in block 21. This entry must be approved in advance by CHNAVPERS (PERS-311).', severity: 'HIGH' },
      { ruleId: 'RULE-021E', category: 'Billet Subcategory', block: '21', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Invalid Billet Subcategory', description: 'Invalid entry in block 21, Billet Subcategory.', severity: 'MEDIUM' },
      
      // SECTION 7: Reporting Senior (Blocks 22-27)
      { ruleId: 'RULE-022-027A', category: 'Reporting Senior', block: '22-27', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Reporting Senior SSN Required', description: "Block 27, Reporting Senior's SSN, is a required entry in order to compute reporting senior's cumulative average.", severity: 'HIGH' },
      { ruleId: 'RULE-022-027B', category: 'Reporting Senior', block: '22-27', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Reporting Senior Consistency', description: 'Blocks 22-27 must be the same on each report.', severity: 'CRITICAL' },
      
      // SECTION 8: Trait Grades (Blocks 33-39)
      { ruleId: 'RULE-033-039A', category: 'Trait Grades', block: '33-39', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Trait Grade Missing', description: 'Block __ is missing a trait grade.', severity: 'MEDIUM' },
      { ruleId: 'RULE-033-039B', category: 'Trait Grades', block: '33-39', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Maximum Trait with Failed PRT (Before 30 APR 2000)', description: 'Maximum trait grade allowed with a failed PRT entry in block 20 is 2.0 (for reports written on or before 30 APR 2000).', severity: 'HIGH' },
      { ruleId: 'RULE-033-039C', category: 'Trait Grades', block: '33-39', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Reservist Training NOB Report', description: 'Submit a Not Observed, Detachment of Individual report (with comments) for reservist on active duty for training that does not exceed 90 days.', severity: 'MEDIUM' },
      
      // SECTION 9: Officer/Chief Comments and Promotion (Blocks 41-47)
      { ruleId: 'RULE-041', category: 'Comments and Promotion', block: '41', active: false, primary: true, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'Comments Cannot Be Handwritten/Centered (Officer/Chief)', description: 'Block 41, Comments on Performance, cannot be handwritten or centered.', severity: 'MEDIUM' },
      { ruleId: 'RULE-042A', category: 'Comments and Promotion', block: '42', active: false, primary: true, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'Promotion Recommendation Required (Officer/Chief)', description: 'Provide individual promotion recommendation in Block 42. Entry required.', severity: 'CRITICAL' },
      { ruleId: 'RULE-042B', category: 'Comments and Promotion', block: '42', active: false, primary: true, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'NOB Without Promotion Justification', description: 'If the report is intended to be an observed report without a promotion recommendation, give the reason in block 41.', severity: 'HIGH' },
      { ruleId: 'RULE-042C', category: 'Comments and Promotion', block: '42', active: false, primary: true, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'NOB Cannot Have Promotion Recommendation', description: 'A promotion recommendation in blocks 42 and 43 may not be given if blocks 33 through 39 are marked not observed (NOB).', severity: 'HIGH' },
      { ruleId: 'RULE-043', category: 'Comments and Promotion', block: '43', active: true, primary: false, enlisted: true, officer: false, chief: false, applicableForms: ['1610/7'], title: 'Comments Cannot Be Handwritten/Centered (Enlisted)', description: 'Block 43, Comments on Performance, cannot be handwritten or centered.', severity: 'MEDIUM' },
      { ruleId: 'RULE-043A', category: 'Comments and Promotion', block: '43', active: true, primary: false, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'Missing Reports from Summary Group', description: 'Missing report(s) from summary group. Summary line, block 43 shows a total of ___ reports. Only ___ reports received.', severity: 'HIGH' },
      { ruleId: 'RULE-043B', category: 'Comments and Promotion', block: '43', active: true, primary: false, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'Trait 2.0 Limits Promotion to Promotable', description: 'The maximum promotion recommendation allowed with any trait graded 2.0 is Promotable. Invalidates entire summary group.', severity: 'HIGH' },
      { ruleId: 'RULE-043C', category: 'Comments and Promotion', block: '43', active: true, primary: false, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'Promotion Quota Exceeded', description: 'Promotion recommendation quota exceeds maximum limit authorized for Must or Early promote. Invalidates entire summary group.', severity: 'CRITICAL' },
      
      // SECTION 10: Enlisted Promotion and Retention (Blocks 45-52)
      { ruleId: 'RULE-045A', category: 'Enlisted Promotion', block: '45', active: true, primary: false, enlisted: true, officer: false, chief: false, applicableForms: ['1610/7'], title: 'Promotion Recommendation Required (Enlisted)', description: 'Provide individual promotion recommendation in Block 45. Entry required.', severity: 'CRITICAL' },
      { ruleId: 'RULE-045B', category: 'Enlisted Promotion', block: '45', active: true, primary: false, enlisted: true, officer: false, chief: false, applicableForms: ['1610/7'], title: 'NOB Without Promotion Justification', description: 'If the report is intended to be an observed report without a promotion recommendation, give the reason in block 43.', severity: 'HIGH' },
      { ruleId: 'RULE-045C', category: 'Enlisted Promotion', block: '45', active: true, primary: false, enlisted: true, officer: false, chief: false, applicableForms: ['1610/7'], title: 'NOB Cannot Have Promotion Recommendation', description: 'A promotion recommendation in blocks 45 and 46 may not be given if blocks 33 through 39 are marked not observed (NOB).', severity: 'HIGH' },
      { ruleId: 'RULE-046A', category: 'Enlisted Promotion', block: '46', active: true, primary: false, enlisted: false, officer: true, chief: true, applicableForms: ['1610/2', '1610/7'], title: 'Member Signature Required (Officer/Chief)', description: 'The report is not signed by the member. The reporting senior may write "Certified Copy Provided" in the member\'s signature block if the report is not adverse.', severity: 'HIGH' },
      { ruleId: 'RULE-047', category: 'Enlisted Promotion', block: '47', active: true, primary: false, enlisted: true, officer: false, chief: false, applicableForms: ['1610/7'], title: 'Enlisted Retention Required', description: 'Entry required in block 47, Enlisted Retention (Enlisted forms only).', severity: 'CRITICAL' },
      { ruleId: 'RULE-050', category: 'Enlisted Promotion', block: '50', active: true, primary: false, enlisted: true, officer: false, chief: false, applicableForms: ['1610/7'], title: 'Reporting Senior Signature Required (Enlisted)', description: 'The report is not signed by the reporting senior.', severity: 'HIGH' },
      
      // SECTION 11: Administrative Notes
      { ruleId: 'RULE-NOTE-2A', category: 'Administrative', block: 'N/A', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Form Not Legible', description: 'Due to a form location error during the processing of the report, the report is not legible for file.', severity: 'HIGH' },
      { ruleId: 'RULE-NOTE-2F', category: 'Administrative', block: 'N/A', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Wrong Form Submitted', description: 'The report is invalid. Submitted on wrong form. Please resubmit.', severity: 'CRITICAL' },
      { ruleId: 'RULE-NOTE-2H', category: 'Administrative', block: 'N/A', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Duplicate Report on File', description: 'Member currently has a report on file. Only one regular report is authorized.', severity: 'HIGH' },
      { ruleId: 'RULE-NOTE-2N', category: 'Administrative', block: '22-27', active: true, primary: false, enlisted: true, officer: true, chief: true, applicableForms: ['1610/2', '1610/5', '1610/7'], title: 'Unauthorized Reporting Senior Grade', description: 'Member listed as reporting senior in blocks 22 through 27 is not authorized based on grade listed in block 23.', severity: 'HIGH' },
      { ruleId: 'RULE-NOTE-2P', category: 'Administrative', block: '42', active: true, primary: false, enlisted: false, officer: true, chief: false, applicableForms: ['1610/2'], title: 'Ensign/LTJG Promotion Limit', description: 'The maximum promotion recommendation allowed for non-LDO Ensign and Lieutenant (Junior Grade) officers is "Promotable".', severity: 'HIGH' }
    ];
  };

  const handleSave = () => {
    // Save settings to localStorage
    localStorage.setItem('confidenceThreshold', confidenceThreshold);
    
    setAlert({ type: 'success', content: 'Settings saved successfully' });
    setTimeout(() => setAlert(null), 3000);
  };

  const getSeverityBadge = (severity) => {
    const config = {
      CRITICAL: { color: 'red', text: 'Critical' },
      HIGH: { color: 'red', text: 'High' },
      MEDIUM: { color: 'blue', text: 'Medium' },
      LOW: { color: 'grey', text: 'Low' }
    };
    const badgeConfig = config[severity] || { color: 'grey', text: severity };
    return <Badge color={badgeConfig.color}>{badgeConfig.text}</Badge>;
  };

  const getStatusBadge = (active) => {
    return active ? 
      <Badge color="green">Active</Badge> : 
      <Badge color="grey">Inactive</Badge>;
  };

  const categories = ['all', ...new Set(businessRules.map(r => r.category))];
  
  const filteredRules = businessRules.filter(rule => {
    const matchesCategory = selectedCategory === 'all' || rule.category === selectedCategory;
    const matchesFilter = 
      rule.title?.toLowerCase().includes(rulesFilterText.toLowerCase()) ||
      rule.ruleId?.toLowerCase().includes(rulesFilterText.toLowerCase()) ||
      rule.description?.toLowerCase().includes(rulesFilterText.toLowerCase());
    return matchesCategory && matchesFilter;
  });

  const rulesByCategory = categories.reduce((acc, category) => {
    if (category === 'all') return acc;
    acc[category] = filteredRules.filter(r => r.category === category);
    return acc;
  }, {});

  const getFormName = (formNumber) => {
    const formNames = {
      '1610/1': 'FITREP/EVAL Summary Letter',
      '1610/2': 'Fitness Report (W2-O6)',
      '1610/5': 'Fitness Report (O7-O8)',
      '1610/7': 'Chief Eval (E7-E9)',
      '1610/19': 'Military Individual Development Plan',
      '1610/20': 'Mid-Term Counseling Checklist'
    };
    return formNames[formNumber] || formNumber;
  };

  const rulesColumnDefinitions = [
    {
      id: 'ruleId',
      header: 'Rule ID',
      cell: item => (
        <Box>
          <Box fontWeight="bold">{item.ruleId}</Box>
          <Box fontSize="body-s" color="text-body-secondary">Block {item.block}</Box>
        </Box>
      ),
      width: 130,
      minWidth: 130
    },
    {
      id: 'title',
      header: 'Rule Title & Description',
      cell: item => (
        <Box>
          <Box fontWeight="bold" margin={{ bottom: 'xxxs' }}>{item.title}</Box>
          <Box 
            fontSize="body-s" 
            color="text-body-secondary"
            style={{ 
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              lineHeight: '1.4',
              maxWidth: '350px'
            }}
          >
            {item.description}
          </Box>
        </Box>
      ),
      width: 350,
      minWidth: 300
    },
    {
      id: 'forms',
      header: 'Forms',
      cell: item => (
        <Box>
          {item.applicableForms && item.applicableForms.length > 0 ? (
            <Box fontSize="body-s">
              {item.applicableForms.map((form, idx) => (
                <Box key={form} margin={{ bottom: 'xxxs' }}>
                  <Box fontWeight="bold">{form}</Box>
                  <Box fontSize="body-s" color="text-body-secondary">
                    {getFormName(form)}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box fontSize="body-s" color="text-body-secondary">All Forms</Box>
          )}
        </Box>
      ),
      width: 160,
      minWidth: 160
    },
    {
      id: 'applicability',
      header: 'Personnel',
      cell: item => (
        <Box fontSize="body-s">
          {item.enlisted && <Box margin={{ bottom: 'xxxs' }}>Enlisted (E1-E6)</Box>}
          {item.chief && <Box margin={{ bottom: 'xxxs' }}>Chief (E7-E9)</Box>}
          {item.officer && <Box margin={{ bottom: 'xxxs' }}>Officer (O1-O6)</Box>}
        </Box>
      ),
      width: 150,
      minWidth: 150
    },
    {
      id: 'severity',
      header: 'Severity',
      cell: item => getSeverityBadge(item.severity),
      width: 110,
      minWidth: 110
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => getStatusBadge(item.active),
      width: 110,
      minWidth: 110
    }
  ];

  return (
    <Tabs
      tabs={[
        {
          label: 'General Settings',
          id: 'general',
          content: (
            <SpaceBetween direction="vertical" size="l">
              {alert && (
                <Alert
                  type={alert.type}
                  dismissible
                  onDismiss={() => setAlert(null)}
                >
                  {alert.content}
                </Alert>
              )}

              <Container
                header={
                  <Header
                    variant="h2"
                    description="Configure application settings"
                  >
                    General Settings
                  </Header>
                }
              >
                <SpaceBetween direction="vertical" size="l">
                  <FormField
                    label="Notification Email"
                    description="Email address for processing notifications"
                  >
                    <Input
                      value={notificationEmail}
                      onChange={({ detail }) => setNotificationEmail(detail.value)}
                      placeholder="user@example.com"
                      type="email"
                    />
                  </FormField>

                  <FormField
                    label="Auto-refresh Interval"
                    description="Automatically refresh submissions list (seconds)"
                  >
                    <Input
                      value={autoRefresh}
                      onChange={({ detail }) => setAutoRefresh(detail.value)}
                      type="number"
                    />
                  </FormField>

                  <Toggle 
                    checked={emailNotifications}
                    onChange={({ detail }) => setEmailNotifications(detail.checked)}
                  >
                    Enable email notifications
                  </Toggle>

                  <Toggle 
                    checked={desktopNotifications}
                    onChange={({ detail }) => setDesktopNotifications(detail.checked)}
                  >
                    Enable desktop notifications
                  </Toggle>
                </SpaceBetween>
              </Container>

              <Container
                header={
                  <Header
                    variant="h2"
                    description="Processing and validation configuration"
                  >
                    Processing Settings
                  </Header>
                }
              >
                <SpaceBetween direction="vertical" size="l">
                  <FormField
                    label="Confidence Threshold"
                    description="Documents with confidence below this value will require manual validation (0-100)"
                  >
                    <Input
                      value={confidenceThreshold}
                      onChange={({ detail }) => setConfidenceThreshold(detail.value)}
                      type="number"
                    />
                  </FormField>

                  <Toggle 
                    checked={autoProcess}
                    onChange={({ detail }) => setAutoProcess(detail.checked)}
                  >
                    Auto-process uploaded documents
                  </Toggle>

                  <Toggle 
                    checked={manualReview}
                    onChange={({ detail }) => setManualReview(detail.checked)}
                  >
                    Require manual review for all submissions
                  </Toggle>
                </SpaceBetween>
              </Container>

              <Button variant="primary" onClick={handleSave}>
                Save Settings
              </Button>
            </SpaceBetween>
          )
        },
        {
          label: 'Business Rules',
          id: 'business-rules',
          content: (
            <SpaceBetween direction="vertical" size="l">
              <SpaceBetween direction="vertical" size="m">
                <Alert type="info" header="About Business Rules">
                  Business rules are automatically applied to all submitted forms during processing. 
                  Rules are based on NAVPERS form validation requirements and ensure compliance with 
                  Navy personnel evaluation standards. Any rule violations will trigger a Quality Control review.
                </Alert>

                <Container
                  header={
                    <Header variant="h3">
                      Understanding Rule Severity Levels
                    </Header>
                  }
                >
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box>
                        <SpaceBetween direction="horizontal" size="xs">
                          <Badge color="red">Critical</Badge>
                          <Box fontSize="body-s" fontWeight="bold">Blocking Issues</Box>
                        </SpaceBetween>
                      </Box>
                      <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxxs' }}>
                        Required fields missing or invalid data that prevents form acceptance. 
                        Examples: Missing SSN, invalid dates, required signatures absent. 
                        These violations will block the form from being accepted into the official record.
                      </Box>
                    </div>
                    
                    <div>
                      <Box>
                        <SpaceBetween direction="horizontal" size="xs">
                          <Badge color="red">High</Badge>
                          <Box fontSize="body-s" fontWeight="bold">Significant Violations</Box>
                        </SpaceBetween>
                      </Box>
                      <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxxs' }}>
                        Important validation failures that may affect member records or promotion eligibility. 
                        Examples: Invalid PRT codes, unauthorized report types, missing justifications. 
                        Requires immediate correction before processing.
                      </Box>
                    </div>
                    
                    <div>
                      <Box>
                        <SpaceBetween direction="horizontal" size="xs">
                          <Badge color="blue">Medium</Badge>
                          <Box fontSize="body-s" fontWeight="bold">Moderate Issues</Box>
                        </SpaceBetween>
                      </Box>
                      <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxxs' }}>
                        Non-blocking issues that should be reviewed but may not prevent form acceptance. 
                        Examples: Missing trait grades, formatting issues, incomplete optional fields. 
                        Should be corrected but may be accepted with manual review.
                      </Box>
                    </div>
                    
                    <div>
                      <Box>
                        <SpaceBetween direction="horizontal" size="xs">
                          <Badge color="grey">Low</Badge>
                          <Box fontSize="body-s" fontWeight="bold">Minor Warnings</Box>
                        </SpaceBetween>
                      </Box>
                      <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxxs' }}>
                        Advisory notices that don't prevent form acceptance. 
                        Examples: Recommendations for improvement, style guidelines, optional enhancements. 
                        For informational purposes only.
                      </Box>
                    </div>
                  </ColumnLayout>
                </Container>

                <Container
                  header={
                    <Header variant="h3">
                      Form Number Reference
                    </Header>
                  }
                >
                  <ColumnLayout columns={3} variant="text-grid">
                    <div>
                      <Box fontWeight="bold">NAVPERS 1610/1</Box>
                      <Box fontSize="body-s" color="text-body-secondary">
                        FITREP/EVAL Summary Letter
                      </Box>
                    </div>
                    <div>
                      <Box fontWeight="bold">NAVPERS 1610/2</Box>
                      <Box fontSize="body-s" color="text-body-secondary">
                        Fitness Report (Officers W2-O6)
                      </Box>
                    </div>
                    <div>
                      <Box fontWeight="bold">NAVPERS 1610/5</Box>
                      <Box fontSize="body-s" color="text-body-secondary">
                        Fitness Report (Flag Officers O7-O8)
                      </Box>
                    </div>
                    <div>
                      <Box fontWeight="bold">NAVPERS 1610/7</Box>
                      <Box fontSize="body-s" color="text-body-secondary">
                        Chief Petty Officer Evaluation (E7-E9)
                      </Box>
                    </div>
                    <div>
                      <Box fontWeight="bold">NAVPERS 1610/19</Box>
                      <Box fontSize="body-s" color="text-body-secondary">
                        Military Individual Development Plan
                      </Box>
                    </div>
                    <div>
                      <Box fontWeight="bold">NAVPERS 1610/20</Box>
                      <Box fontSize="body-s" color="text-body-secondary">
                        Mid-Term Counseling Checklist
                      </Box>
                    </div>
                  </ColumnLayout>
                </Container>
              </SpaceBetween>

              <Container
                header={
                  <Header
                    variant="h2"
                    description="View all active business rules applied during form validation"
                    counter={`(${filteredRules.length})`}
                    actions={
                      <Button
                        iconName="refresh"
                        onClick={loadBusinessRules}
                        loading={loadingRules}
                      >
                        Refresh Rules
                      </Button>
                    }
                  >
                    Business Rules Configuration
                  </Header>
                }
              >
                <SpaceBetween direction="vertical" size="m">
                  <ColumnLayout columns={4} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Total Rules</Box>
                      <Box fontSize="display-l" fontWeight="bold" padding={{ top: 'xxxs' }}>
                        {businessRules.length}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Active Rules</Box>
                      <Box fontSize="display-l" fontWeight="bold" padding={{ top: 'xxxs' }}>
                        {businessRules.filter(r => r.active).length}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Critical Rules</Box>
                      <Box fontSize="display-l" fontWeight="bold" padding={{ top: 'xxxs' }}>
                        {businessRules.filter(r => r.severity === 'CRITICAL').length}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Categories</Box>
                      <Box fontSize="display-l" fontWeight="bold" padding={{ top: 'xxxs' }}>
                        {categories.length - 1}
                      </Box>
                    </div>
                  </ColumnLayout>

                  <TextFilter
                    filteringText={rulesFilterText}
                    onChange={({ detail }) => setRulesFilterText(detail.filteringText)}
                    filteringPlaceholder="Search rules by ID, title, or description..."
                  />
                </SpaceBetween>
              </Container>

              <SpaceBetween direction="vertical" size="m">
                {Object.entries(rulesByCategory).map(([category, rules]) => (
                  <ExpandableSection
                    key={category}
                    headerText={category}
                    headerCounter={`(${rules.length})`}
                    defaultExpanded={category === 'Member Identification'}
                  >
                    <Table
                      columnDefinitions={rulesColumnDefinitions}
                      items={rules}
                      loading={loadingRules}
                      loadingText="Loading business rules..."
                      variant="embedded"
                      empty={
                        <Box textAlign="center" color="inherit">
                          <b>No rules in this category</b>
                        </Box>
                      }
                    />
                  </ExpandableSection>
                ))}
              </SpaceBetween>

              {selectedCategory === 'all' && filteredRules.length === 0 && !loadingRules && (
                <Box textAlign="center" padding={{ vertical: 'xxl' }}>
                  <Box variant="strong">No rules match your search</Box>
                  <Box variant="p" color="text-body-secondary">
                    Try adjusting your search criteria
                  </Box>
                </Box>
              )}
            </SpaceBetween>
          )
        }
      ]}
    />
  );
}

export default Settings;
